import { useCallback, useRef, useEffect } from 'react'
import axios from 'axios'

export const useLoadProgress = ({
  pid,
  ac,
  tk,
  choiceIndexMapRef,
  choicesOrderRef,
  bringOptionsMapRef,
  otherAnswers,
  noneAnswers,
  matrixAnswers,
  timestamps,
  // === Agree.js 要用的 ===
  surveyModel,
  convertedSurvey,
  isReady, // Survey 元件 render 完成
  requiresLogin, // { login: boolean }
  setIsLoading,
  sfdSamplerId,
  webAuthType = 0,
}) => {
  const host = process.env.REACT_APP_HOST
  const LoadAnswersUrl = '/clientWeb/v1/LoadAnswers'

  const progressDataRef = useRef(null)

  // discord 通知
  const sendDiscordNotification = async (message) => {
    try {
      await axios.post('/api/send-notification', {
        content: message,
      })
    } catch (error) {
      console.error('Failed', error)
    }
  }

  // 載入儲存的答案
  const loadSavedAnswers = useCallback(
    async (pid) => {
      try {
        const response = await axios.post(
          host + LoadAnswersUrl,
          { pid: pid },
          {
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
              Accept: '*/*',
              ac: ac,
              tk: tk,
            },
            timeout: 30000,
          }
        )

        console.log('LoadAnswers response', response.data)

        if (response.data.answers) {
          progressDataRef.current = response.data

          return response.data.answers
        } else {
          console.log('沒有找到儲存的進度或進度為空')

          return null
        }
      } catch (error) {
        console.error('載入進度失敗:', error)

        // 檢查是否為網路錯誤或 timeout
        const isNetworkError =
          error.code === 'ECONNABORTED' || // axios timeout
          error.message.includes('timeout') || // 一般 timeout 訊息
          error.message.includes('Network Error') || // 網路錯誤
          !error.response // 沒有伺服器回應

        if (isNetworkError) {
          alert(
            '載入問卷進度時網路連線異常或逾時，請檢查網路狀況後重新整理頁面'
          )
          return null
        }

        // 有正常 API 回應的錯誤
        if (error.response && error.response.data) {
          alert('載入問卷內容時發生錯誤，請重新整理頁面後再試一次')
        } else {
          // 其他未知錯誤
          alert('發生未知錯誤，請重新整理頁面後再試一次')
        }

        sendDiscordNotification(
          `EZC Web LoadAnswers Error \n Error: ${error} \n pid: ${pid} \n ac: ${ac} \n tk: ${tk} \n Response: ${error.response?.data?.msg} \n Response Code: ${error.response?.data?.code}`
        )
        return null
      }
    },
    [ac, tk, sendDiscordNotification]
  )

  // 設置問卷的起始頁面
  const setStartingPage = useCallback((survey, savedAnswers) => {
    if (!survey || !savedAnswers || savedAnswers.length === 0) return false

    // 延遲執行，確保 setValue 與跳題邏輯完成
    setTimeout(() => {
      try {
        // 1️⃣ 先執行條件，讓不可見頁面隱藏
        survey.runConditions()

        // 2️⃣ 找第一個「可見且未填答」的頁面
        const targetPageName = findFirstUnfilledPageName(survey)
        if (!targetPageName) {
          console.log('📝 沒找到未填答頁面，保持在當前位置')
          return
        }

        // 3️⃣ 找到在 pages 陣列中的索引 (含隱藏頁)
        const pageIndexAll = survey.pages.findIndex(
          (p) => p.name === targetPageName
        )
        if (pageIndexAll === -1) return

        // 4️⃣ 計算 pageIndexAll 之前被隱藏掉的頁數
        const skippedCount = survey.pages
          .slice(0, pageIndexAll)
          .filter((p) => !p.isVisible).length

        // 5️⃣ 真正要跳轉的可見索引
        const visibleIndex = pageIndexAll - skippedCount

        survey.currentPageNo = visibleIndex
        console.log(
          `✅ 透過索引跳轉到: ${targetPageName} (全部索引: ${pageIndexAll}, ` +
            `隱藏頁: ${skippedCount}, 可見索引: ${visibleIndex})`
        )
      } catch (err) {
        console.error('設置起始頁面時發生錯誤:', err)
      }
    }, 500) // 這裡的 500 ms 視需要可調整

    return true
  }, [])

  // 找到第一個有未填答問題的可見頁面名稱
  const findFirstUnfilledPageName = (survey) => {
    // 強制更新頁面可見性
    survey.runConditions()

    console.log('=== 尋找未填答頁面 ===')

    for (let i = 0; i < survey.pages.length; i++) {
      const page = survey.pages[i]

      if (!page.isVisible) {
        console.log(`${page.name}: 頁面不可見，跳過`)
        continue
      }

      // 檢查頁面中的問題
      const visibleQuestions = page.questions.filter((q) => q.isVisible)
      // [FIX] 只檢查"必填"且"為空"的問題，忽略純圖片或非必填項
      const emptyQuestions = visibleQuestions.filter(
        (q) => q.isEmpty() && q.isRequired
      )

      // console.log('visibleQuestions', visibleQuestions)
      // console.log('emptyQuestions', emptyQuestions)
      console.log(
        `${page.name}: 可見問題 ${visibleQuestions.length} 個，未填答 ${emptyQuestions.length} 個`
      )

      // 如果有未填答問題，就是目標頁面
      if (emptyQuestions.length > 0) {
        console.log(`🎯 找到目標頁面: ${page.name}`)
        return page.name
      }
    }

    // 如果沒有未填答問題，返回最後一個可見頁面
    for (let i = survey.pages.length - 1; i >= 0; i--) {
      const page = survey.pages[i]
      if (page.isVisible) {
        console.log(`🎯 沒有未填答問題，使用最後可見頁面: ${page.name}`)
        return page.name
      }
    }

    console.log('❌ 沒有找到任何可見頁面')
    return null
  }

  const progressLoadedRef = useRef(false)

  useEffect(() => {
    const loadProgressForSurveyModel = async () => {
      // 避免重複載入
      if (progressLoadedRef.current) return

      // 確保必要的條件都滿足
      if (
        !surveyModel ||
        !convertedSurvey ||
        !ac ||
        !tk ||
        (!requiresLogin?.login && !sfdSamplerId && !(webAuthType > 0)) ||
        !isReady
      ) {
        return
      }

      try {
        progressLoadedRef.current = true
        setIsLoading(true)

        console.log('開始為 surveyModel 載入進度...')

        // 載入後端的答案資料
        const savedAnswers = await loadSavedAnswers(pid)

        if (savedAnswers && savedAnswers.length > 0) {
          console.log('找到儲存的答案:', savedAnswers)

          // 簡化的轉換邏輯（暫時不依賴 receivedSurvey）
          const surveyData = {}
          const commentData = {}

          savedAnswers.forEach((answerItem) => {
            const questionName = `q${answerItem.qi}`

            // 先檢查這個問題在 surveyModel 中的類型
            const question = surveyModel.getQuestionByName(questionName)
            if (!question) {
              console.warn(`找不到問題 ${questionName}`)
              return
            }

            console.log(
              `處理問題 ${questionName}, 類型: ${question.getType()}, 答案:`,
              answerItem.ans
            )

            // [FIX 9EZ-200] 修 merge fix 帶來的副作用：
            // 修法後送出的 ans 是「畫面顯示順序的 index」(displayIndex)，
            // 而 SurveyJS choices 的 value 是 originalIndex。若直接 pass-through
            // 給 surveyData，隨機題會還原到錯誤選項。
            // 解法：backend 一律回傳 ord（display → originalIndex 陣列），用 ord[ans]
            // 反查 originalIndex；ord 末端的 length-1 / length-2 位置由 useSubmitAnswers
            // 寫成 none/other 的 marker，這裡翻回 SurveyJS 認得的 'none'/'other' 字串。
            // 缺 ord 的 fallback（舊資料 / 異常情況）保留原 .none/.other 比對邏輯。
            if (
              Array.isArray(answerItem.ans) &&
              Array.isArray(answerItem.ord) &&
              answerItem.ord.length > 0
            ) {
              const ord = answerItem.ord
              const hasNone = question.showNoneItem === true
              const hasOther = question.showOtherItem === true
              const noneDisplayIdx = hasNone ? ord.length - 1 : -1
              const otherDisplayIdx = hasOther
                ? hasNone
                  ? ord.length - 2
                  : ord.length - 1
                : -1
              answerItem.ans = answerItem.ans.map((val) => {
                if (val === 'none' || val === 'other') return val
                if (typeof val !== 'number') return val
                if (val === noneDisplayIdx) return 'none'
                if (val === otherDisplayIdx) return 'other'
                if (val >= 0 && val < ord.length) {
                  const orig = ord[val]
                  return typeof orig === 'number' ? orig : val
                }
                return val
              })
            } else if (choiceIndexMapRef?.current?.[questionName]) {
              const map = choiceIndexMapRef.current[questionName]
              if (answerItem.ans && Array.isArray(answerItem.ans)) {
                answerItem.ans = answerItem.ans.map((val) => {
                  if (map.none !== undefined && val === map.none) return 'none'
                  if (map.other !== undefined && val === map.other)
                    return 'other'
                  return val
                })
              }
            }

            // 根據問題類型處理答案
            if (question.getType() === 'matrix') {
              // 矩陣題特殊處理
              if (answerItem.ans && answerItem.ans.length > 0) {
                const matrixValue = {}

                // 獲取矩陣的行（rows）
                const rows = question.rows

                answerItem.ans.forEach((answerArray, rowIndex) => {
                  if (rows[rowIndex]) {
                    // 使用 row 的 value 作為 key，而不是 text
                    const rowKey = rows[rowIndex].value

                    if (Array.isArray(answerArray) && answerArray.length > 0) {
                      matrixValue[rowKey] = answerArray[0]
                    } else if (typeof answerArray === 'number') {
                      matrixValue[rowKey] = answerArray
                    }
                  }
                })

                console.log(`矩陣題 ${questionName} 轉換結果:`, matrixValue)
                console.log(`矩陣題 ${questionName} 的行定義:`, rows)
                surveyData[questionName] = matrixValue
              }
            } else {
              // 其他題型的處理
              if (answerItem.ans && answerItem.ans.length > 0) {
                let answerValue

                // 檢查是否有 other 答案
                const hasOther =
                  answerItem.other && Object.keys(answerItem.other).length > 0
                // 判斷多選題
                const isMultiSelect =
                  question.getType() === 'checkbox' ||
                  (question.getType() === 'imagepicker' && question.multiSelect)
                if (hasOther) {
                  // 半開放題：需要特殊處理 other 選項
                  // TODO:
                  console.log(
                    `處理半開放題 ${questionName}, other:`,
                    answerItem.other
                  )

                  // 將答案中的 other 索引轉換為 'other'
                  const otherIndex = parseInt(Object.keys(answerItem.other)[0])
                  const otherText = Object.values(answerItem.other)[0]

                  if (answerItem.ans.length === 1) {
                    // 單選題
                    answerValue =
                      answerItem.ans[0] === otherIndex
                        ? 'other'
                        : answerItem.ans[0]
                  } else {
                    // 多選題
                    answerValue = answerItem.ans.map((ans) =>
                      ans === otherIndex ? 'other' : ans
                    )
                  }

                  // 先設定主答案
                  surveyData[questionName] = answerValue
                  // 將評論答案分開處理
                  console.log('otherText', otherText)
                  console.log('typeof otherText', typeof otherText)
                  // 🔧 修復:處理 other 文字,確保為字串
                  if (otherText !== undefined && otherText !== null) {
                    let commentValue = otherText

                    // 如果是物件,嘗試提取值
                    if (typeof otherText === 'object') {
                      const values = Object.values(otherText)
                      commentValue = values.length > 0 ? values[0] : ''
                    }

                    // 強制轉換為字串,並確保不是 'undefined' 或 'null' 字串
                    commentValue = String(commentValue)
                    if (
                      commentValue === 'undefined' ||
                      commentValue === 'null'
                    ) {
                      commentValue = ''
                    }

                    // 只有非空字串才加入 commentData
                    if (commentValue.trim()) {
                      commentData[questionName] = commentValue
                      console.log(
                        `✓ 準備設定 comment ${questionName}:`,
                        commentValue
                      )
                    }
                  }
                  // commentData[`${questionName}`] = Object.values(otherText)[0]

                  console.log('commentData', commentData)
                } else {
                  // 沒有 other 的一般題型
                  if (isMultiSelect) {
                    // ✔️ 不管幾個答案，保持陣列形態
                    answerValue = answerItem.ans // [0]、[2,5]...
                  } else if (answerItem.ans.length === 1) {
                    answerValue = answerItem.ans[0]
                  } else {
                    answerValue = answerItem.ans
                  }

                  // [FIX] 如果是 comment 題型，強制轉字串，避免數值造成 trim 錯誤
                  if (
                    question.getType() === 'comment' &&
                    answerValue !== undefined &&
                    answerValue !== null
                  ) {
                    answerValue = String(answerValue)
                  }

                  surveyData[questionName] = answerValue
                }
              }
            }
          })

          console.log('轉換後的主答案:', surveyData)
          console.log('轉換後的評論答案:', commentData)

          // 第一階段：設定主答案
          Object.entries(surveyData).forEach(([questionName, answer]) => {
            try {
              // [FIX] 強制將所有數值轉為字串，避免 SurveyJS 將未匹配的數值選項當作 comment 時發生 trim 錯誤
              let safeAnswer = answer
              if (typeof answer === 'number') {
                safeAnswer = String(answer)
              } else if (Array.isArray(answer)) {
                safeAnswer = answer.map((v) =>
                  typeof v === 'number' ? String(v) : v
                )
              }

              surveyModel.setValue(questionName, safeAnswer)

              console.log(`✅ 成功設定主答案 ${questionName}:`, safeAnswer)
            } catch (error) {
              console.error(`❌ 設定主答案 ${questionName} 失敗:`, error)
            }
          })

          // TODO: 設置起始頁面
          const startPageSet = setStartingPage(surveyModel, savedAnswers)
          if (startPageSet) {
            console.log('已設置問卷從進度頁面開始')
          }

          // 第二階段：延遲設定 Comment 答案（確保主答案已經生效）
          if (Object.keys(commentData).length > 0) {
            console.log('⏳ 準備設定評論答案...', commentData)

            // 使用更長的延遲，確保主答案已完全生效並觸發了相關的事件
            setTimeout(() => {
              Object.entries(commentData).forEach(([k, v]) => {
                try {
                  // 🔧 最終驗證:確保是字串且非空
                  const commentValue = String(v || '').trim()

                  if (!commentValue) {
                    console.log(`⚠️ 跳過空的評論 ${k}`)
                    return
                  }

                  surveyModel.setComment(k, commentValue)

                  // 驗證設定是否成功
                  const setComment = surveyModel.getComment(k)
                  console.log(`📝 驗證評論設定結果 ${k}:`, setComment)
                  console.log(
                    `📝📝 驗證評論設定結果 2 ${k}:`,
                    surveyModel.getComment(k)
                  )
                } catch (error) {
                  console.error(`❌ 設定評論 ${k} 時發生錯誤:`, error)
                  console.error('錯誤詳情:', error.stack)
                }
              })
            }, 200) // 增加延遲時間到 200ms
          } else {
            console.log('ℹ️ 沒有評論答案需要設定')
          }

          // 強制更新所有問題的UI
          setTimeout(() => {
            try {
              surveyModel.getAllQuestions().forEach((question) => {
                if (question.value !== undefined) {
                  question.value = question.value
                }
              })

              if (surveyModel.render) {
                surveyModel.render()
              }

              const currentPageNo = surveyModel.currentPageNo
              surveyModel.currentPageNo = currentPageNo

              console.log('強制UI更新完成')
            } catch (error) {
              console.error('強制UI更新失敗:', error)
            }
          }, 200)

          // [FIX] 全局檢查並修復所有問題的 comment
          setTimeout(() => {
            try {
              console.log('🔍 執行全局 comment 檢查...')
              const allQuestions = surveyModel.getAllQuestions()
              allQuestions.forEach((q) => {
                const comment = surveyModel.getComment(q.name)
                if (
                  comment !== undefined &&
                  comment !== null &&
                  typeof comment !== 'string'
                ) {
                  console.warn(
                    `⚠️ 發現非字串 comment 於 ${q.name}:`,
                    comment,
                    typeof comment
                  )
                  surveyModel.setComment(q.name, String(comment))
                  console.log(`✅ 已修正 ${q.name} comment 為字串`)
                }
              })
            } catch (err) {
              console.error('全局 comment 檢查失敗:', err)
            }
          }, 300)

          // 檢查設定結果
          setTimeout(() => {
            console.log('載入完成後的問卷資料:', surveyModel.data)
            console.log('進度載入完成！')
          }, 600)
        } else {
          console.log('沒有找到儲存的進度')
        }
      } catch (error) {
        console.error('載入進度失敗:', error)
        progressLoadedRef.current = false // 失敗時重置，允許重試
      } finally {
        setIsLoading(false)
      }
    }

    // 延遲執行，確保 surveyModel 完全初始化
    if (surveyModel && isReady) {
      setTimeout(() => {
        loadProgressForSurveyModel()
      }, 100)
    }
  }, [
    surveyModel,
    convertedSurvey,
    ac,
    tk,
    loadSavedAnswers,
    setStartingPage,
    pid,
    requiresLogin?.login,
    setIsLoading,
    isReady,
    sfdSamplerId,
    webAuthType,
  ])

  return {
    loadSavedAnswers,
    // convertBackendAnswersToSurveyFormat,
    // applyProgressToSurveyWithSetValue,
    // loadAndApplyProgress,
    progressDataRef,

    setStartingPage,
  }
}
